import type { ImageVariantUrls } from './types';

/**
 * Pixels; covers the admin's small thumbnails at 2x, and the srcset takes the
 * display variant beyond.
 */
export const IMAGE_THUMB_WIDTH = 480;

/**
 * Pixels; the variant a lightbox shows.
 */
export const IMAGE_DISPLAY_WIDTH = 1200;

/**
 * Pixels, narrowest first: every WebP variant the worker makes of a picture.
 * Each is made for every picture, an original narrower than a width giving a
 * file at the original's own size, so each key always exists.
 */
export const CATALOG_IMAGE_WIDTHS: readonly number[] = [
  320,
  IMAGE_THUMB_WIDTH,
  640,
  960,
  IMAGE_DISPLAY_WIDTH,
  1600,
];

// Variants are immutable, so a new width takes a new key; thumb and display keep the keys they shipped with.
const SUFFIX_BY_WIDTH = new Map([
  [IMAGE_THUMB_WIDTH, `thumb-${IMAGE_THUMB_WIDTH}`],
  [IMAGE_DISPLAY_WIDTH, 'display'],
]);

const VARIANT_SUFFIX = /_(?:thumb-\d+|display|w\d+)\.webp$/;

const STOCKX_IMAGE_HOST = 'images.stockx.com';

// Pixels: StockX renders min(w, 800) x dpr with dpr capped at 3, so a wider file is 800 at a fractional
// dpr (960 is 1.2) and 2400 is the widest it serves.
const STOCKX_BASE_WIDTH = 800;

// The folders item_image and unit_image rows are stored in, whose pictures the worker makes every width of.
const CATALOG_IMAGE_PATH = /^\/catalog\/(?:cards|colors|items|products|units)\/[^/]+\/[^/]+$/;

/**
 * True for a picture stored in a folder the worker makes our sizes for, by its
 * path alone: a variant asked of a picture on another host 404s, and the kit
 * Image then falls back to the url as given.
 */
export function isCatalogImageUrl(url: string): boolean {
  try {
    return CATALOG_IMAGE_PATH.test(new URL(url, 'https://relative.invalid/').pathname);
  } catch {
    return false;
  }
}

/**
 * Null when neither variant exists; the caller then shows the original.
 */
export function imageVariantSrcSet({ displayUrl, thumbUrl }: ImageVariantUrls): string | null {
  const candidates = [
    thumbUrl ? `${thumbUrl} ${IMAGE_THUMB_WIDTH}w` : null,
    displayUrl ? `${displayUrl} ${IMAGE_DISPLAY_WIDTH}w` : null,
  ].filter((candidate) => candidate !== null);
  return candidates.length > 0 ? candidates.join(', ') : null;
}

/**
 * A storage key or URL path for one width: `a/b.jpg` at 640 is `a/b_w640.webp`,
 * at 480 `a/b_thumb-480.webp`, at 1200 `a/b_display.webp`. Takes the original
 * or any of its variants.
 */
export function catalogImageVariantPath(path: string, width: number): string {
  const suffix = SUFFIX_BY_WIDTH.get(width) ?? `w${width}`;
  const variant = VARIANT_SUFFIX.exec(path);
  if (variant) return `${path.slice(0, variant.index)}_${suffix}.webp`;
  const dot = path.lastIndexOf('.');
  const base = dot > path.lastIndexOf('/') ? path.slice(0, dot) : path;
  return `${base}_${suffix}.webp`;
}

/**
 * The URL of a picture's variant at one of `CATALOG_IMAGE_WIDTHS`, from the
 * original's URL or any variant's (the api sends the display one).
 */
export function catalogImageVariantUrl(url: string, width: number): string {
  const end = url.search(/[?#]/);
  return catalogImageVariantPath(end === -1 ? url : url.slice(0, end), width);
}

/**
 * The widths worth listing: all of them when the original's width is unknown
 * (null, or 0), else those narrower than it plus the first that holds it
 * whole.
 */
export function catalogImageWidths(originalWidth: number | null | undefined): number[] {
  if (!originalWidth) return [...CATALOG_IMAGE_WIDTHS];
  const fits = CATALOG_IMAGE_WIDTHS.findIndex((width) => width >= originalWidth);
  return fits === -1 ? [...CATALOG_IMAGE_WIDTHS] : CATALOG_IMAGE_WIDTHS.slice(0, fits + 1);
}

/**
 * A `srcset` over `catalogImageWidths`; a variant made from a narrower original
 * is described at the original's width, the size of the file.
 */
export function catalogImageSrcSet(url: string, originalWidth: number | null | undefined): string {
  return catalogImageWidths(originalWidth)
    .map((width) => {
      const described = originalWidth ? Math.min(width, originalWidth) : width;
      return `${catalogImageVariantUrl(url, width)} ${described}w`;
    })
    .join(', ');
}

/**
 * A `srcset` over `CATALOG_IMAGE_WIDTHS` for a StockX picture, whose CDN sizes
 * the file from the query, so the link's own query and fragment are dropped;
 * null for any other host.
 */
export function stockxImageSrcSet(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.hostname !== STOCKX_IMAGE_HOST) return null;
  const base = `${parsed.origin}${parsed.pathname}`;
  return CATALOG_IMAGE_WIDTHS.map((width) => {
    const query =
      width <= STOCKX_BASE_WIDTH
        ? `w=${width}`
        : `w=${STOCKX_BASE_WIDTH}&dpr=${width / STOCKX_BASE_WIDTH}`;
    return `${base}?${query} ${width}w`;
  }).join(', ');
}
